import React, { useEffect, lazy, Suspense } from 'react';
import { BrowserRouter, Routes, Route, Navigate, useLocation } from 'react-router-dom';
import { AuthProvider } from './context/AuthContext';
import { CartProvider } from './context/CartContext';
import { EditModeProvider } from './context/EditModeContext';
import { Toaster } from 'sonner';
import MaisonNav from './components/MaisonNav';
import DianaChat from './components/DianaChat';
import EditToggle from './components/EditToggle';
import ErrorBoundary from './components/ErrorBoundary';
import PopupHost from './components/PopupHost';
import ConsentBanner from './components/ConsentBanner';
import { initTracking, trackPageView } from './lib/tracking';
import Home from './pages/Home';
import { installAxiosRefresh } from './lib/axiosSetup';

// Install global 401 → refresh → retry interceptor exactly once
installAxiosRefresh();

// Lazy-load all secondary pages → much smaller initial bundle
const Products = lazy(() => import('./pages/Products'));
const ProductDetail = lazy(() => import('./pages/ProductDetail'));
const Login = lazy(() => import('./pages/Login'));
const Register = lazy(() => import('./pages/Register'));
const ForgotPassword = lazy(() => import('./pages/ForgotPassword'));
const ResetPassword = lazy(() => import('./pages/ResetPassword'));
const Checkout = lazy(() => import('./pages/Checkout'));
const OrderSuccess = lazy(() => import('./pages/OrderSuccess'));
const Orders = lazy(() => import('./pages/Orders'));
const Account = lazy(() => import('./pages/Account'));
const Wishlist = lazy(() => import('./pages/Wishlist'));
const Admin = lazy(() => import('./pages/Admin'));
const POS = lazy(() => import('./pages/POS'));
const BarcodeSheet = lazy(() => import('./pages/BarcodeSheet'));
const Affiliate = lazy(() => import('./pages/Affiliate'));
const TrackOrder = lazy(() => import('./pages/TrackOrder'));
const Atelier = lazy(() => import('./pages/Atelier'));
const AtelierPage = lazy(() => import('./pages/AtelierPage'));
const AtelierDesign = lazy(() => import('./pages/AtelierDesign'));
const Court = lazy(() => import('./pages/Court'));
const LegalPage = lazy(() => import('./pages/LegalPage'));
const RoomPage = lazy(() => import('./pages/RoomPage'));
const FormPage = lazy(() => import('./pages/FormPage'));

import '@/App.css';

const PageFallback = () => (
  <div className="min-h-screen pt-32 flex items-center justify-center">
    <p className="overline tracking-[0.3em] text-[#595956]">Loading...</p>
  </div>
);

const NotFound = () => (
  <div className="min-h-screen pt-32 px-6 flex flex-col items-center justify-center text-center">
    <p className="overline tracking-[0.3em] text-[#B08D57] mb-3">404</p>
    <h1 className="text-4xl mb-4" style={{fontFamily: "'Playfair Display', serif"}}>Page not found</h1>
    <p className="text-[#595956] mb-8 max-w-md">The page you were looking for is no longer available.</p>
    <a href="/" className="btn-luxe">Return Home</a>
  </div>
);

function App() {
  return (
    <AuthProvider>
      <CartProvider>
        <EditModeProvider>
        <BrowserRouter>
          <RefCapture />
          <PageTracker />
          <div className="min-h-screen bg-ivory text-charcoal" style={{fontFamily: "'Manrope', sans-serif"}}>
            <MaisonNav />
            <ErrorBoundary resetKey={window.location.pathname}>
              <Suspense fallback={<PageFallback />}>
                <Routes>
                  <Route path="/" element={<Home />} />
                  <Route path="/products" element={<Products />} />
                  <Route path="/products/:id" element={<ProductDetail />} />
                  <Route path="/login" element={<Login />} />
                  <Route path="/forgot-password" element={<ForgotPassword />} />
                  <Route path="/reset-password" element={<ResetPassword />} />
                  <Route path="/register" element={<Register />} />
                  <Route path="/checkout" element={<Checkout />} />
                  <Route path="/order-success" element={<OrderSuccess />} />
                  <Route path="/orders" element={<Orders />} />
                  <Route path="/account" element={<Account />} />
                  <Route path="/wishlist" element={<Wishlist />} />
                  <Route path="/admin" element={<Admin />} />
                  <Route path="/pos" element={<POS />} />
                  <Route path="/barcode-sheet" element={<BarcodeSheet />} />
                  <Route path="/affiliate" element={<Affiliate />} />
                  <Route path="/track" element={<TrackOrder />} />
                  <Route path="/atelier" element={<AtelierPage />} />
                  <Route path="/atelier/appointment" element={<Atelier />} />
                  <Route path="/atelier/design/:slug" element={<AtelierDesign />} />
                  <Route path="/court" element={<Court />} />
                  <Route path="/terms" element={<LegalPage docKey="terms" />} />
                  <Route path="/privacy" element={<LegalPage docKey="privacy" />} />
                  <Route path="/returns" element={<LegalPage docKey="returns" />} />
                  <Route path="/rooms/:room" element={<RoomPage />} />
                  <Route path="/f/:slug" element={<FormPage />} />
                  {/* Legacy /lab/* URLs redirect home (Lab deprecated) */}
                  <Route path="/lab/*" element={<Navigate to="/" replace />} />
                  <Route path="*" element={<NotFound />} />
                </Routes>
              </Suspense>
            </ErrorBoundary>
            <PopupHost />
            <ConsentBanner />
            <DianaChat />
            <EditToggle />
            <Toaster position="bottom-right" toastOptions={{ style: { background: '#1B1815', color: '#FAF6EF', border: 'none', borderRadius: 0, fontFamily: "'Manrope', sans-serif", fontSize: '13px' } }} />
          </div>
        </BrowserRouter>
        </EditModeProvider>
      </CartProvider>
    </AuthProvider>
  );
}

// One canonical page_view per route change; destinations are loaded once at boot.
const PageTracker = () => {
  const location = useLocation();
  const ready = React.useRef(false);
  useEffect(() => {
    initTracking().then(() => { ready.current = true; trackPageView(window.location.pathname); });
  }, []);
  useEffect(() => {
    if (ready.current) trackPageView(location.pathname);
  }, [location.pathname]);
  return null;
};

// Capture ?ref=CODE into localStorage and fire click tracking
const RefCapture = () => {
  const location = useLocation();
  useEffect(() => {
    const params = new URLSearchParams(location.search);
    const ref = params.get('ref');
    if (ref) {
      const data = { code: ref.toUpperCase(), expires: Date.now() + 30 * 24 * 60 * 60 * 1000 };
      localStorage.setItem('diosa_referral', JSON.stringify(data));
      fetch(`${process.env.REACT_APP_BACKEND_URL}/api/affiliates/track-click/${ref.toUpperCase()}`, { method: 'POST' }).catch(() => {});
    }
  }, [location.search]);
  return null;
};

export default App;
